/**
 * The only part of a GraphQL error this app reads.
 *
 * Declared structurally rather than imported from `graphql`, because the errors
 * reaching here arrive as three different concrete types depending on the
 * transport (a `GraphQLError` instance off an ApolloQueryResult, a plain object
 * off an HttpErrorResponse body, a hand-built one here) and all that is ever
 * inspected is `message` and `extensions`, which is exactly what
 * `resolveGraphQlStatusCode` reads.
 */
export interface GraphQlErrorLike {
  readonly message?: string;
  readonly extensions?: Record<string, any>;
}

/**
 * A GraphQL failure that arrived as a RESOLVED result rather than a rejection.
 *
 * Apollo is configured app-wide with `errorPolicy: 'all'` for queries
 * (graphql.module.ts). Under that policy a resolver that throws does NOT reject
 * the observable: the query resolves with `data: null` and the failure sitting
 * in `errors`. Nothing downstream was reading `errors`, so every session query
 * treated a denial as a successful answer.
 *
 * `graphQLErrors` is named to match ApolloError, because that is the shape
 * `SessionInterceptor.isRefreshTokenRejected` and `resolveGraphQlStatusCode`
 * already know how to classify. Throwing anything else, a bare TypeError say,
 * is invisible to them, and an unclassifiable refresh failure is what pinned a
 * member to the 403 page (CPD-652).
 */
export class GraphQlResultError extends Error {
  public readonly graphQLErrors: readonly GraphQlErrorLike[];

  constructor(operation: string, errors: readonly GraphQlErrorLike[]) {
    const detail = (errors ?? [])
      .map((e) => e?.message)
      .filter(Boolean)
      .join(' | ');

    super(detail || `${operation} failed`);
    this.name = 'GraphQlResultError';
    this.graphQLErrors = errors ?? [];
  }
}

/**
 * Read one field off an Apollo query result, turning a GraphQL failure into a
 * thrown error instead of a null nobody checked.
 *
 * WHY THIS EXISTS, AND WHAT IT IS FIXING.
 *
 * `refreshSession` used to be written as `results.data.refreshSession`. When the
 * gateway rejected the refresh token, `data` was null and that line threw
 * `TypeError: Cannot read properties of null (reading 'refreshSession')`, the
 * exact message members saw on the 403 page. A TypeError carries no
 * `graphQLErrors`, no `error.errors` and no `status`, so the interceptor could
 * not tell a rejected token from a dropped connection, declined to delete the
 * dead token, and the lockout became permanent.
 *
 * `getSession` had the mirror-image fault. It read through with optional
 * chaining, so a rejected token produced a perfectly well-formed EMPTY session
 * and no error at all: the app marked itself initialised holding no token, and
 * the next query's 403 spent a refresh the member never needed. That is the
 * unexplained `refreshSession` seen on opening a second tab.
 *
 * Both are the same mistake, and this is the single place that now refuses to
 * make it: if the server reported errors, they are raised as errors.
 */
export function readGraphQlResult<TData, TField extends keyof TData>(
  operation: string,
  result: {
    data?: TData | null;
    errors?: readonly GraphQlErrorLike[];
  },
  field: TField
): NonNullable<TData[TField]> {
  const value = result?.data?.[field];

  if (value !== null && value !== undefined) {
    /*
     * THE PAYLOAD WINS, EVEN IF SOMETHING ELSE IN THE RESPONSE FAILED.
     *
     * `errorPolicy: 'all'` also delivers PARTIAL results: a session that
     * resolved perfectly well alongside an error from some sibling field. The
     * session is real, so raising here would throw away a working session over
     * an unrelated failure, which is the opposite of the point. Only the
     * absence of the payload is treated as a failure.
     */
    return value as NonNullable<TData[TField]>;
  }

  if (result?.errors?.length) {
    throw new GraphQlResultError(operation, result.errors);
  }

  /*
   * No errors and no payload either. This should not happen, but it is exactly
   * the case that used to throw a TypeError and take the session down with it,
   * so it gets a named error rather than a crash.
   */
  throw new GraphQlResultError(operation, [
    { message: `${operation} returned no data` }
  ]);
}
